import { AccordionDetails, AccordionSummary, DialogContent, FormControl, InputAdornment, Stack, CircularProgress } from '@mui/material'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import FrameworkRemovedTooltip from '../framework-removed-tooltip/FrameworkRemovedTooltip'
import { OnyxiaTooltip } from 'ui/components/tooltip'
import { FC, useEffect, useMemo, useState } from 'react'
import {
  CustomizedDialog,
  CloseButton,
  StepButton,
  StepTitle,
  SwitchLabel,
  StyledAccordion,
  StyledDialogTitle,
  ControlLabel,
  ControlValue,
  LoadingOverlay,
  StyledDialogActions
} from './customizeReportDialog.styles'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TextFieldV2 from '../../../../components/form-components-v2/text-field-v2'
import SelectV2 from '../../../../components/form-components-v2/select-v2'
import SelectWithPopoverMenuV2 from '../../../../components/form-components-v2/select-with-popover-menu-v2'
import SwitchV2 from 'ui/components/form-components-v2/switch-v2'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { FREQUENCY_OPTIONS, TIME_FRAME_OPTIONS } from './customizeReportDialog.configs'
import {
  TreeSelectOption,
  ReportType,
  CreateReportRequest,
  CreateReportTemplateRequest,
  PerformanceReportConfig,
  ReportTemplateResponse,
  ValidateTemplateConfigResponse,
  ValidationErrorType
} from 'models'
import { CpiServiceV3, FrameworkServiceV3, TenantServiceV3, ReportTemplateService } from 'services'
import { useAppDispatch } from 'store/hooks'
import { openToast, closeToast, ToastVariant } from 'store/toasts/toastSlice'
import SelectedItemsDisplay from '../selected-items-display/SelectedItemsDisplay'
import CheckIcon from '@mui/icons-material/Check'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import theme from 'theme'
import ThemeButton from 'ui/components/buttons/primary/ThemeButton'
import { formatEndDate, formatStartDate } from 'lib/dateUtils'
import { generateAndDownloadFromTemplate, generateAndDownloadReport, getApiErrorDescription } from '../../reports-v2.utils'

enum Step {
  None = -1,
  Details = 0,
  Scope = 1,
  TimeAndFrequency = 2
}

enum FormField {
  ReportName = 'reportName',
  ReportDesc = 'reportDesc',
  Framework = 'framework',
  CategoryAndCPIs = 'categoryAndCpis',
  IncludeCPIAppendix = 'isIncludeCPIAppendix',
  IncludePublicLabels = 'isIncludePublicLabels',
  TimeFrame = 'timeFrame',
  Frequency = 'frequency'
}

interface CustomizeReportDialogProps {
  isOpen: boolean
  onClose: () => void
  onTemplateSaved?: (generatedTemplateId?: string) => void
  editTemplate?: ReportTemplateResponse | null
  validationResult?: ValidateTemplateConfigResponse
  onFrameworkChange?: () => Promise<void>
}

const CustomizeReportDialog: FC<CustomizeReportDialogProps> = ({
  isOpen,
  onClose,
  onTemplateSaved,
  editTemplate,
  validationResult,
  onFrameworkChange
}) => {
  const isEditMode = Boolean(editTemplate)

  const invalidItems = useMemo(() => {
    if (!validationResult?.errors) return new Set<string>()
    return new Set(
      validationResult.errors
        .filter((e) => e.type === ValidationErrorType.CATEGORY || e.type === ValidationErrorType.METRIC)
        .map((e) => e.id)
    )
  }, [validationResult])

  const hasFrameworkError = useMemo(
    () => validationResult?.errors?.some((e) => e.type === ValidationErrorType.FRAMEWORK) ?? false,
    [validationResult]
  )
  const dispatch = useAppDispatch()
  const [activeStep, setActiveStep] = useState(Step.Details)
  const [categoryAndCpiOptions, setCategoryAndCpiOptions] = useState<TreeSelectOption[]>([])
  const [frameworkDisplayName, setFrameworkDisplayName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [visitedSteps, setVisitedSteps] = useState<Set<Step>>(new Set([Step.Details]))
  const [categoryAndCpisTouched, setCategoryAndCpisTouched] = useState(false)

  const formik = useFormik({
    initialValues: {
      [FormField.ReportName]: '',
      [FormField.ReportDesc]: '',
      [FormField.Framework]: '',
      [FormField.CategoryAndCPIs]: [],
      [FormField.IncludeCPIAppendix]: false,
      [FormField.IncludePublicLabels]: false,
      [FormField.TimeFrame]: '3m',
      [FormField.Frequency]: 'daily'
    },
    validationSchema: yup.object({
      [FormField.ReportName]: yup.string().required('Required'),
      [FormField.ReportDesc]: yup.string(),
      [FormField.Framework]: yup.string().required('Required'),
      [FormField.CategoryAndCPIs]: yup.array().min(1, 'At least one category or CPI is required'),
      [FormField.TimeFrame]: yup.string().required('Required'),
      [FormField.Frequency]: yup.string().required('Required')
    }),
    onSubmit: async (values) => {
      const categories = extractSelectedCategoriesAndCpis(values[FormField.CategoryAndCPIs])
      const { dateFrom, dateTo, timePeriod } = constructDateRange(values[FormField.TimeFrame])

      const request: CreateReportRequest = {
        name: values.reportName,
        description: values.reportDesc || undefined,
        type: ReportType.PERFORMANCE_REPORT,
        config: {
          reportName: values.reportName,
          reportDesc: values.reportDesc || undefined,
          framework: values.framework,
          categories,
          frequency: values.frequency,
          dateFrom,
          dateTo,
          timePeriod,
          isIncludeCPIAppendix: values.isIncludeCPIAppendix,
          isIncludePublicLabels: values.isIncludePublicLabels,
          type: 'general'
        }
      }

      try {
        setIsSubmitting(true)
        await generateAndDownloadReport(request)
        handleClose()
      } catch (error) {
        dispatch(
          openToast({
            variant: ToastVariant.error,
            props: {
              text: 'Failed to Generate Report',
              description: getApiErrorDescription(error)
            }
          })
        )
        setTimeout(() => dispatch(closeToast()), 5000)
      } finally {
        setIsSubmitting(false)
      }
    }
  })

  const getStepWithError = (errors: Record<string, unknown>): Step | null => {
    if (errors[FormField.ReportName]) {
      return Step.Details
    }

    if (errors[FormField.CategoryAndCPIs]) {
      return Step.Scope
    }

    if (errors[FormField.TimeFrame] || errors[FormField.Frequency]) {
      return Step.TimeAndFrequency
    }

    return null
  }

  const setTouchedForStep = (step: Step) => {
    if (step === Step.Details) {
      formik.setTouched({
        [FormField.ReportName]: true
      } as any)
    } else if (step === Step.Scope) {
      formik.setTouched({
        [FormField.CategoryAndCPIs]: true
      } as any)
    } else if (step === Step.TimeAndFrequency) {
      formik.setTouched({
        [FormField.TimeFrame]: true,
        [FormField.Frequency]: true
      } as any)
    }
  }

  const buildReportConfig = (): PerformanceReportConfig => {
    const values = formik.values
    const categories = extractSelectedCategoriesAndCpis(values[FormField.CategoryAndCPIs])
    const { dateFrom, dateTo, timePeriod } = constructDateRange(values[FormField.TimeFrame])

    return {
      reportName: values.reportName,
      reportDesc: values.reportDesc || undefined,
      framework: values.framework,
      categories,
      frequency: values.frequency,
      dateFrom,
      dateTo,
      timePeriod,
      isIncludeCPIAppendix: values.isIncludeCPIAppendix,
      isIncludePublicLabels: values.isIncludePublicLabels,
      type: 'general'
    }
  }

  const validateAndRun = async (action: () => Promise<void>) => {
    const errors = await formik.validateForm()
    const hasErrors = !!Object.keys(errors).length

    if (hasErrors) {
      const stepWithError = getStepWithError(errors)
      if (stepWithError) {
        setTouchedForStep(stepWithError)
        setActiveStep(stepWithError)
      }
    } else {
      await action()
    }
  }

  const handleDownload = async () => {
    await validateAndRun(() => formik.handleSubmit() as unknown as Promise<void>)
  }

  const handleSaveAsTemplate = async () => {
    await validateAndRun(async () => {
      const config = buildReportConfig()

      try {
        setIsSavingTemplate(true)
        if (isEditMode && editTemplate) {
          await ReportTemplateService.updateTemplate(editTemplate.id, {
            name: formik.values.reportName,
            description: formik.values.reportDesc || undefined,
            config
          })
          dispatch(
            openToast({
              variant: ToastVariant.success,
              props: {
                text: 'Template Updated',
                description: 'Your report template has been updated successfully.'
              }
            })
          )
        } else {
          const request: CreateReportTemplateRequest = {
            name: formik.values.reportName,
            description: formik.values.reportDesc || undefined,
            type: ReportType.PERFORMANCE_REPORT,
            config
          }
          await ReportTemplateService.createTemplate(request)
          dispatch(
            openToast({
              variant: ToastVariant.success,
              props: {
                text: 'Template Saved',
                description: 'Your report template has been saved successfully.'
              }
            })
          )
        }
        setTimeout(() => dispatch(closeToast()), 5000)
        onTemplateSaved?.()
        handleClose()
      } catch (error) {
        dispatch(
          openToast({
            variant: ToastVariant.error,
            props: {
              text: isEditMode ? 'Failed to Update Template' : 'Failed to Save Template',
              description: getApiErrorDescription(error)
            }
          })
        )
        setTimeout(() => dispatch(closeToast()), 5000)
      } finally {
        setIsSavingTemplate(false)
      }
    })
  }

  const handleDownloadAndSave = async () => {
    await validateAndRun(async () => {
      const config = buildReportConfig()
      const templateRequest: CreateReportTemplateRequest = {
        name: formik.values.reportName,
        description: formik.values.reportDesc || undefined,
        type: ReportType.PERFORMANCE_REPORT,
        config
      }

      try {
        setIsSubmitting(true)
        const template = await ReportTemplateService.createTemplate(templateRequest)
        await generateAndDownloadFromTemplate(template.id, template.name)
        onTemplateSaved?.(template.id)
        handleClose()
      } catch (error) {
        dispatch(
          openToast({
            variant: ToastVariant.error,
            props: {
              text: 'Failed to Download and Save Template',
              description: getApiErrorDescription(error)
            }
          })
        )
        setTimeout(() => dispatch(closeToast()), 5000)
      } finally {
        setIsSubmitting(false)
      }
    })
  }

  const isDetailsStepComplete = (): boolean => {
    const hasReportName = Boolean(formik.values[FormField.ReportName])
    return hasReportName
  }

  const isScopeStepComplete = (): boolean => {
    const hasFramework = Boolean(formik.values[FormField.Framework])
    const hasCategoryOrCPI = formik.values[FormField.CategoryAndCPIs].length > 0
    return hasFramework && hasCategoryOrCPI
  }

  const isTimeAndFrequencyStepComplete = (): boolean => {
    const hasTimeFrame = Boolean(formik.values[FormField.TimeFrame])
    const hasFrequency = Boolean(formik.values[FormField.Frequency])
    return hasTimeFrame && hasFrequency
  }

  const canAccessStep = (step: Step): boolean => {
    if (step === Step.Details) {
      return true
    }

    if (step === Step.Scope) {
      return isDetailsStepComplete()
    }

    if (step === Step.TimeAndFrequency) {
      return isDetailsStepComplete() && isScopeStepComplete()
    }

    return false
  }

  const handleChangeStep = (step: Step) => {
    const isAccessible = canAccessStep(step)
    if (!isAccessible) {
      return
    }

    const newStep = step !== activeStep ? step : Step.None
    setActiveStep(newStep)

    if (newStep !== Step.None) {
      setVisitedSteps((prev) => new Set(prev).add(newStep))
    }
  }

  const handleClose = () => {
    onClose()
    setActiveStep(Step.Details)
    setVisitedSteps(new Set([Step.Details]))
    setFrameworkDisplayName('')
    formik.resetForm()
    setCategoryAndCpisTouched(false)
  }

  const extractSelectedCategoriesAndCpis = (selectedTreeOptions: TreeSelectOption[]) => {
    const allCategoriesSelected =
      selectedTreeOptions.length === categoryAndCpiOptions.length &&
      selectedTreeOptions.every((selected) => {
        const original = categoryAndCpiOptions.find((opt) => opt.value === selected.value)
        return original && selected.children?.length === original.children?.length
      })

    if (allCategoriesSelected) {
      return []
    }

    return selectedTreeOptions.map((treeOption) => {
      const original = categoryAndCpiOptions.find((opt) => opt.value === treeOption.value)
      const allCpisSelected = original && treeOption.children?.length === original.children?.length

      return {
        id: treeOption.value,
        metrics: allCpisSelected ? [] : treeOption.children?.map((child) => child.value) || []
      }
    })
  }

  const isCustomDateRange = formik.values[FormField.TimeFrame]?.includes('to')

  const constructDateRange = (timeFrame: string) => {
    let dateFrom: string | null
    let dateTo: string | null
    let timePeriod: string | null
    const isCustomTime = timeFrame.includes('to')

    if (isCustomTime) {
      const [start, end] = timeFrame.split(' to ')
      dateFrom = formatStartDate(start)
      dateTo = formatEndDate(end)
      timePeriod = null
    } else {
      dateFrom = null
      dateTo = null
      timePeriod = timeFrame
    }

    return { dateFrom, dateTo, timePeriod }
  }

  const prefillFormFromTemplate = async (
    config: PerformanceReportConfig,
    options: TreeSelectOption[],
    currentInvalidItems: Set<string>
  ) => {
    formik.setFieldValue(FormField.ReportName, config.reportName || '')
    formik.setFieldValue(FormField.ReportDesc, config.reportDesc || '')
    formik.setFieldValue(FormField.IncludeCPIAppendix, config.isIncludeCPIAppendix ?? false)
    formik.setFieldValue(FormField.IncludePublicLabels, config.isIncludePublicLabels ?? false)
    formik.setFieldValue(FormField.Frequency, config.frequency || 'daily')

    if (config.timePeriod) {
      formik.setFieldValue(FormField.TimeFrame, config.timePeriod)
    } else if (config.dateFrom && config.dateTo) {
      formik.setFieldValue(FormField.TimeFrame, `${config.dateFrom} to ${config.dateTo}`)
    }

    if (config.categories && config.categories.length > 0) {
      const updatedOptions = [...options]

      // Fetch display names for invalid metrics
      const invalidMetricIds = config.categories.flatMap((cat) => cat.metrics).filter((m) => currentInvalidItems.has(m))
      const metricLabels: Record<string, string> = {}
      if (invalidMetricIds.length > 0) {
        const results = await Promise.allSettled(invalidMetricIds.map((id) => CpiServiceV3.getCpiDetails(id)))
        results.forEach((result, idx) => {
          const metricId = invalidMetricIds[idx]
          if (result.status === 'fulfilled') {
            metricLabels[metricId] = `${result.value.display_name} ${result.value.title}`
          } else {
            metricLabels[metricId] = metricId
          }
        })
      }

      const selectedOptions: TreeSelectOption[] = config.categories
        .map((cat) => {
          let categoryOption = updatedOptions.find((opt) => opt.value === cat.id)

          // Create placeholder for invalid/deactivated category
          if (!categoryOption && currentInvalidItems.has(cat.id)) {
            categoryOption = {
              label: '[removed]',
              value: cat.id,
              children: cat.metrics.map((m) => ({
                label: metricLabels[m] || m,
                value: m
              }))
            }
            updatedOptions.push(categoryOption)
          }

          if (!categoryOption) return null

          const resolvedOption = categoryOption
          let children: TreeSelectOption[]
          if (cat.metrics.length > 0) {
            children = cat.metrics
              .map((metricId) => {
                const existing = resolvedOption.children?.find((child) => child.value === metricId)
                if (!existing && currentInvalidItems.has(metricId)) {
                  return { label: metricLabels[metricId] || metricId, value: metricId }
                }
                return existing
              })
              .filter(Boolean) as TreeSelectOption[]
          } else {
            children = categoryOption.children || []
          }

          return {
            label: categoryOption.label,
            value: categoryOption.value,
            children
          }
        })
        .filter(Boolean) as TreeSelectOption[]

      // Also inject invalid metric placeholders into the options tree for dropdown display
      if (currentInvalidItems.size > 0) {
        config.categories.forEach((cat) => {
          const optionEntry = updatedOptions.find((opt) => opt.value === cat.id)
          if (!optionEntry) return
          cat.metrics.forEach((metricId) => {
            if (currentInvalidItems.has(metricId) && !optionEntry.children?.some((c) => c.value === metricId)) {
              optionEntry.children = [
                ...(optionEntry.children || []),
                { label: metricLabels[metricId] || metricId, value: metricId }
              ]
            }
          })
        })
      }

      setCategoryAndCpiOptions(updatedOptions)
      formik.setFieldValue(FormField.CategoryAndCPIs, selectedOptions)
    } else {
      formik.setFieldValue(FormField.CategoryAndCPIs, options)
    }

    setVisitedSteps(new Set([Step.Details, Step.Scope, Step.TimeAndFrequency]))
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const init = async () => {
      try {
        setIsLoading(true)
        const tenant = await TenantServiceV3.getTenant()

        // In edit mode, use the template's saved framework; otherwise use tenant's current framework
        const framework = editTemplate?.config?.framework || tenant.cyFramework
        const isStandardFramework = ['nist', 'onyxia'].includes(framework)

        const [categoriesResult, cpisResult] = await Promise.allSettled([
          FrameworkServiceV3.searchFrameworkCategories({ framework }),
          CpiServiceV3.searchCpis({ framework, status: 'active', sortBy: 'desc' })
        ])
        const categories = categoriesResult.status === 'fulfilled' ? categoriesResult.value : []
        const cpis = cpisResult.status === 'fulfilled' ? cpisResult.value : []

        if (hasFrameworkError) {
          setFrameworkDisplayName('[removed]')
        } else if (isStandardFramework) {
          setFrameworkDisplayName(framework)
        } else {
          try {
            const customFramework = await FrameworkServiceV3.getCustomFramework()
            setFrameworkDisplayName(customFramework.title || '[removed]')
          } catch {
            setFrameworkDisplayName('[removed]')
          }
        }

        const options: TreeSelectOption[] = categories
          .map((category) => {
            const categoryCpis = cpis
              .filter((cpi) => cpi.category?.id === category.id)
              .sort((cpi1, cpi2) => {
                const numA = parseInt(cpi1.display_name.replace('CPI-', ''), 10)
                const numB = parseInt(cpi2.display_name.replace('CPI-', ''), 10)
                return numA - numB
              })

            return {
              label: category.title,
              value: category.id,
              children: categoryCpis.map((cpi) => ({
                label: `${cpi.display_name} ${cpi.title}`,
                value: cpi.name
              }))
            }
          })
          .filter((option) => option.children.length > 0)
        setCategoryAndCpiOptions(options)
        formik.setFieldValue(FormField.Framework, framework)

        if (editTemplate?.config) {
          await prefillFormFromTemplate(editTemplate.config, options, invalidItems)
        }
      } catch (error) {
        setCategoryAndCpiOptions([])
      } finally {
        setIsLoading(false)
      }
    }

    init()
  }, [isOpen, editTemplate])

  return (
    <>
      <CustomizedDialog
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        disableRestoreFocus
        maxWidth={false}
      >
        {isLoading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <StyledDialogTitle>{isEditMode ? 'Edit Program Template' : 'Customize Program Report'}</StyledDialogTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </Stack>
        <DialogContent>
          <form id="downloadForm" onSubmit={formik.handleSubmit}>
            <Stack direction="column" spacing={1}>
              {/* Step 1: Details */}
              <StyledAccordion
                expanded={activeStep === Step.Details}
                onChange={() => handleChangeStep(Step.Details)}
                className={isDetailsStepComplete() && activeStep !== Step.Details ? 'fake-disabled' : ''}
              >
                <AccordionSummary id="panel1-header" aria-controls="panel1-content" expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <StepButton>
                      {isDetailsStepComplete() && activeStep !== Step.Details && visitedSteps.has(Step.Details) ? (
                        <CheckIcon />
                      ) : (
                        '1'
                      )}
                    </StepButton>
                    <StepTitle>Details</StepTitle>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="column" spacing={2}>
                    <TextFieldV2
                      id={FormField.ReportName}
                      label="Report name"
                      variant="outlined"
                      placeholder="Enter report name"
                      autoComplete="off"
                      maxLength={50}
                      InputLabelProps={{ shrink: true }}
                      error={formik.touched[FormField.ReportName] && Boolean(formik.errors[FormField.ReportName])}
                      helperText={formik.touched[FormField.ReportName] && formik.errors[FormField.ReportName]}
                      {...formik.getFieldProps(FormField.ReportName)}
                    />
                    <TextFieldV2
                      id={FormField.ReportDesc}
                      multiline
                      rows={4}
                      variant="outlined"
                      label="Report description"
                      placeholder="Enter a description..."
                      autoComplete="off"
                      maxLength={100}
                      InputLabelProps={{ shrink: true }}
                      {...formik.getFieldProps(FormField.ReportDesc)}
                    />
                    <ControlLabel>Report format</ControlLabel>
                    <ControlValue>PDF</ControlValue>
                  </Stack>
                </AccordionDetails>
              </StyledAccordion>
              {/* Step 2: Scope */}
              <StyledAccordion
                expanded={activeStep === Step.Scope}
                onChange={() => handleChangeStep(Step.Scope)}
                disabled={!canAccessStep(Step.Scope)}
                className={isScopeStepComplete() && activeStep !== Step.Scope ? 'fake-disabled' : ''}
              >
                <AccordionSummary id="panel2-header" aria-controls="panel2-content" expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <StepButton>
                      {isScopeStepComplete() && activeStep !== Step.Scope && visitedSteps.has(Step.Scope) ? (
                        <CheckIcon />
                      ) : (
                        '2'
                      )}
                    </StepButton>
                    <StepTitle>Scope</StepTitle>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="column" spacing={2}>
                    <TextFieldV2
                      id={FormField.Framework}
                      label="Framework"
                      variant="outlined"
                      placeholder="Enter framework"
                      autoComplete="off"
                      disabled={true}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: theme.colors.btnSecondaryDisabledText,
                          textTransform: 'capitalize'
                        }
                      }}
                      {...formik.getFieldProps(FormField.Framework)}
                      value={frameworkDisplayName}
                      InputProps={
                        hasFrameworkError
                          ? {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FrameworkRemovedTooltip
                                    message="The framework used for this template is no longer available. You can use this template with your current framework."
                                    onFrameworkChange={onFrameworkChange}
                                  >
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                      <ExclamationCircleIcon
                                        width={18}
                                        height={18}
                                        color={theme.colorTokens.info.attention}
                                      />
                                    </span>
                                  </FrameworkRemovedTooltip>
                                </InputAdornment>
                              )
                            }
                          : undefined
                      }
                    />
                    <SelectWithPopoverMenuV2
                      searchable={true}
                      label="Category & CPIs"
                      placeholder="Select categories and CPIs"
                      id={FormField.CategoryAndCPIs}
                      options={categoryAndCpiOptions}
                      value={formik.values[FormField.CategoryAndCPIs]}
                      selectAllCheckboxLabel="All Categories"
                      error={categoryAndCpisTouched && formik.values[FormField.CategoryAndCPIs].length === 0}
                      helperText="Required"
                      onClose={() => setCategoryAndCpisTouched(true)}
                      onChange={(newValue) => formik.setFieldValue(FormField.CategoryAndCPIs, newValue)}
                      invalidItems={invalidItems}
                    />
                    {/* Display selected values as chips */}
                    {formik.values[FormField.CategoryAndCPIs].length > 0 && (
                      <SelectedItemsDisplay
                        value={formik.values[FormField.CategoryAndCPIs]}
                        allOptions={categoryAndCpiOptions}
                        parentType="Category"
                        childType="CPI"
                        onChange={(newValue: TreeSelectOption[]) =>
                          formik.setFieldValue(FormField.CategoryAndCPIs, newValue)
                        }
                        invalidItems={invalidItems}
                      />
                    )}
                    <FormControl fullWidth>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                        <SwitchLabel>Include CPI Appendix</SwitchLabel>
                        <SwitchV2
                          id={FormField.IncludeCPIAppendix}
                          checked={formik.values[FormField.IncludeCPIAppendix]}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Stack>
                    </FormControl>
                    <FormControl fullWidth>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                        <SwitchLabel>Include Public Labels</SwitchLabel>
                        <SwitchV2
                          id={FormField.IncludePublicLabels}
                          checked={formik.values[FormField.IncludePublicLabels]}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Stack>
                    </FormControl>
                  </Stack>
                </AccordionDetails>
              </StyledAccordion>
              {/* Step 3: Time & Frequency */}
              <StyledAccordion
                expanded={activeStep === Step.TimeAndFrequency}
                onChange={() => handleChangeStep(Step.TimeAndFrequency)}
                disabled={!canAccessStep(Step.TimeAndFrequency)}
                className={
                  isTimeAndFrequencyStepComplete() &&
                  activeStep !== Step.TimeAndFrequency &&
                  visitedSteps.has(Step.TimeAndFrequency)
                    ? 'fake-disabled'
                    : ''
                }
              >
                <AccordionSummary id="panel3-header" aria-controls="panel3-content" expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <StepButton>
                      {isTimeAndFrequencyStepComplete() &&
                      activeStep !== Step.TimeAndFrequency &&
                      visitedSteps.has(Step.TimeAndFrequency) ? (
                        <CheckIcon />
                      ) : (
                        '3'
                      )}
                    </StepButton>
                    <StepTitle>Time & Frequency</StepTitle>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="column" spacing={2}>
                    <SelectV2
                      id={FormField.TimeFrame}
                      label="Time frame"
                      options={isEditMode ? TIME_FRAME_OPTIONS.filter((o) => o.value !== 'custom') : TIME_FRAME_OPTIONS}
                      isAllowedCustomMonthPicker={!isEditMode}
                      error={formik.touched[FormField.TimeFrame] && Boolean(formik.errors[FormField.TimeFrame])}
                      helperText={formik.touched[FormField.TimeFrame] && formik.errors[FormField.TimeFrame]}
                      {...formik.getFieldProps(FormField.TimeFrame)}
                    />
                    <SelectV2
                      id={FormField.Frequency}
                      label="Frequency"
                      options={FREQUENCY_OPTIONS}
                      error={formik.touched[FormField.Frequency] && Boolean(formik.errors[FormField.Frequency])}
                      helperText={formik.touched[FormField.Frequency] && formik.errors[FormField.Frequency]}
                      {...formik.getFieldProps(FormField.Frequency)}
                    />
                  </Stack>
                </AccordionDetails>
              </StyledAccordion>
            </Stack>
          </form>
        </DialogContent>
        <StyledDialogActions>
          {isEditMode ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <ThemeButton themevariant="secondary" shape="rectangular" onClick={handleClose} sx={{ flex: 1 }}>
                Cancel
              </ThemeButton>
              {/*              <DialogSecondaryButton type="button" onClick={handleClose} sx={{ flex: 1 }}>
                Cancel
              </DialogSecondaryButton> */}
              <ThemeButton
                themevariant="primary"
                shape="rectangular"
                type="button"
                onClick={handleSaveAsTemplate}
                disabled={isSavingTemplate || !formik.isValid || isCustomDateRange}
                sx={{ flex: 1 }}
              >
                {isSavingTemplate ? <CircularProgress size={18} color="primary" /> : 'Save'}
              </ThemeButton>
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <OnyxiaTooltip arrow placement="bottom-end" title="Save as a Template">
                <span>
                  <ThemeButton
                    themevariant="secondary"
                    shape="rectangular"
                    onClick={handleSaveAsTemplate}
                    disabled={isSubmitting || isSavingTemplate || !formik.isValid || !formik.dirty || isCustomDateRange}
                  >
                    {isSavingTemplate ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                  </ThemeButton>
                  {/*                  <DialogSecondaryButton
                    type="button"
                    onClick={handleSaveAsTemplate}
                    disabled={isSubmitting || isSavingTemplate || !formik.isValid || !formik.dirty || isCustomDateRange}
                  >
                    {isSavingTemplate ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                  </DialogSecondaryButton> */}
                </span>
              </OnyxiaTooltip>

              <Stack direction="row" spacing={1}>
                <ThemeButton
                  themevariant="secondary"
                  shape="rectangular"
                  onClick={handleDownloadAndSave}
                  disabled={isSubmitting || isSavingTemplate || !formik.isValid || !formik.dirty || isCustomDateRange}
                >
                  {isSubmitting ? <CircularProgress size={18} /> : 'Download and Save as Template'}
                </ThemeButton>
                {/*                <DialogSecondaryButton
                  type="button"
                  onClick={handleDownloadAndSave}
                  disabled={isSubmitting || isSavingTemplate || !formik.isValid || !formik.dirty || isCustomDateRange}
                >
                  {isSubmitting ? <CircularProgress size={18} /> : 'Download and Save as Template'}
                </DialogSecondaryButton> */}

                <ThemeButton
                  themevariant="primary"
                  shape="rectangular"
                  type="button"
                  onClick={handleDownload}
                  disabled={isSubmitting || isSavingTemplate || !formik.isValid || !formik.dirty}
                >
                  {isSubmitting && !isSavingTemplate ? <CircularProgress size={18} color="primary" /> : 'Download'}
                </ThemeButton>
              </Stack>
            </Stack>
          )}
        </StyledDialogActions>
      </CustomizedDialog>
    </>
  )
}

export default CustomizeReportDialog
